import { useState } from 'react';
import { cn } from 'lib/cn';
import { ChevronDownIcon, ImageIcon } from 'components/icons';
import { formatMoney, formatDate, humanizeEnum } from 'lib/formatters';

function ItemThumb({ src, alt, className }) {
  if (src) {
    return <img src={src} alt={alt || ''} loading="lazy" className={cn('object-cover', className)} />;
  }
  return (
    <span className={cn('flex items-center justify-center bg-surface-100 text-slate-300', className)}>
      <ImageIcon className="h-4 w-4" />
    </span>
  );
}

function FeeRow({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={cn('text-slate-500', strong && 'font-medium text-slate-700')}>{label}</dt>
      <dd className={cn('text-slate-700', strong && 'font-semibold text-slate-900')}>
        {formatMoney(value)}
      </dd>
    </div>
  );
}

/**
 * One payout, expandable to reveal the fee/tax breakdown and the line items
 * (with images, quantity, status and per-item payout/earnings).
 */
export function PayoutCard({ payout }) {
  const [open, setOpen] = useState(false);
  const items = payout.items || [];

  return (
    <div className="panel overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-4 text-left"
      >
        <div className="flex shrink-0 -space-x-2">
          {items.length > 0 ? (
            items.slice(0, 3).map((item, index) => (
              <ItemThumb
                key={index}
                src={item.itemImageUrl}
                alt={item.itemName}
                className="h-9 w-9 rounded-lg ring-2 ring-white"
              />
            ))
          ) : (
            <ItemThumb className="h-9 w-9 rounded-lg" />
          )}
          {items.length > 3 && (
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-200 text-xs font-medium text-slate-600 ring-2 ring-white">
              +{items.length - 3}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">#{payout.orderNumber || '—'}</p>
          <p className="text-xs text-slate-500">
            {items.length} item{items.length === 1 ? '' : 's'}
            {payout.expectedPayoutAt ? ` · Expected ${formatDate(payout.expectedPayoutAt)}` : ''}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">
            {formatMoney(payout.vendorPayoutAmount)}
          </span>
          <ChevronDownIcon
            className={cn('h-4 w-4 text-slate-400 transition-transform', open && 'rotate-180')}
          />
        </div>
      </button>

      {open && (
        <div className="space-y-4 border-t border-surface-200 px-4 py-3">
          <dl className="space-y-1 text-xs">
            <FeeRow label="Order amount" value={payout.orderAmount} />
            <FeeRow label="Delivery fee" value={payout.deliveryFee} />
            <FeeRow label="Handling fee" value={payout.handlingFee} />
            <FeeRow label="Platform fee" value={payout.platformFee} />
            <FeeRow label="Total tax" value={payout.totalTax} />
            <div className="my-1 border-t border-surface-200" />
            <FeeRow label="Your payout" value={payout.vendorPayoutAmount} strong />
          </dl>

          {items.length > 0 && (
            <ul className="divide-y divide-surface-200">
              {items.map((item, index) => (
                <li key={index} className="flex items-center gap-3 py-2">
                  <ItemThumb
                    src={item.itemImageUrl}
                    alt={item.itemName}
                    className="h-10 w-10 shrink-0 rounded-lg"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {item.itemName || 'Item'}
                    </p>
                    <p className="text-xs text-slate-500">
                      Qty {item.quantity}
                      {item.status ? ` · ${humanizeEnum(item.status)}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-slate-900">
                      {formatMoney(item.payoutAmount)}
                    </p>
                    {item.totalEarnings != null && (
                      <p className="text-xs text-slate-400">Earned {formatMoney(item.totalEarnings)}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default PayoutCard;
